import { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Divider,
  Text,
  ScrollArea,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useQuery } from "@tanstack/react-query";
import { rideTypesApi } from "../../api/rideTypes";
import { UploadField } from "../../components/UploadField";
import { en, parseBackendDate } from "../../lib/format";
import type { Driver } from "../../types/domain";
import {
  licenseError,
  normalizeLicense,
  normalizePlate,
  plateError,
} from "./latraFormats";

export interface DriverEditValues {
  name: string;
  email: string;
  phoneNumber: string;
  gender: string;
  dob: Date | null;
  profilePicUrl: string;
  make: string;
  model: string;
  color: string;
  vehicleCategoryID: string;
  licenseNumber: string;
  licenseExpiry: Date | null;
  licenseFront: string;
  registrationNumber: string;
  chassisNumber: string;
  insuranceExpiry: Date | null;
  insuranceImage: string;
  d4Expiry: Date | null;
  d4Image: string;
}

// Backend stores dob AND every document expiry date as "DD-MM-YYYY". Reading
// those with `new Date("08-10-2026")` yields an Invalid Date (see
// `parseBackendDate` in lib/format), and writing them back as ISO fails the
// backend's `^\d{2}-\d{2}-\d{4}$` validation — so the form round-trips through
// `parseBackendDate` (load) and `formatBackendDate` (save).
function formatBackendDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}
// Drop undefined/empty keys so we never $set blank values over real ones.
function clean<T extends Record<string, unknown>>(o: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o))
    if (v !== undefined && v !== "" && v !== null) out[k] = v;
  return out as Partial<T>;
}

// Maps form values → PATCH /driver/:id body (UpdateDriverDto). The backend now
// flattens this to dot-notation, so partial fields merge (no wiped siblings).
export function toDriverUpdateBody(v: DriverEditValues) {
  // Build each document sub-object, then keep only the ones that carry data.
  // `documents` is flattened to dot-notation server-side, so partial edits (e.g.
  // just a new image) merge without wiping the other fields.
  const licenseDetails = clean({
    licenseNumber: normalizeLicense(v.licenseNumber),
    expiryDate: formatBackendDate(v.licenseExpiry),
    frontImageUrl: v.licenseFront,
  });
  const vehicleInsuranceDetails = clean({
    expiryDate: formatBackendDate(v.insuranceExpiry),
    imageUrl: v.insuranceImage,
  });
  const latraVehicleLicenseD4Details = clean({
    expiryDate: formatBackendDate(v.d4Expiry),
    imageUrl: v.d4Image,
  });
  const documents = clean({
    licenseDetails: Object.keys(licenseDetails).length ? licenseDetails : undefined,
    vehicleInsuranceDetails: Object.keys(vehicleInsuranceDetails).length
      ? vehicleInsuranceDetails
      : undefined,
    latraVehicleLicenseD4Details: Object.keys(latraVehicleLicenseD4Details).length
      ? latraVehicleLicenseD4Details
      : undefined,
  });

  return {
    personalDetails: clean({
      name: v.name.trim(),
      email: v.email.trim(),
      phoneNumber: v.phoneNumber.trim(),
      gender: v.gender,
      dob: formatBackendDate(v.dob),
      profilePicUrl: v.profilePicUrl,
    }),
    // Plate number is a vehicle attribute, not a document.
    vehicleDetails: clean({
      make: v.make.trim(),
      model: v.model.trim(),
      color: v.color.trim(),
      vehicleCategoryID: v.vehicleCategoryID,
      registrationNumber: normalizePlate(v.registrationNumber),
      chassisNumber: v.chassisNumber.trim(),
    }),
    ...(Object.keys(documents).length ? { documents } : {}),
  };
}

export function DriverEditModal({
  opened,
  onClose,
  driver,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  driver: Driver | null;
  loading: boolean;
  onSubmit: (values: DriverEditValues) => void;
}) {
  const [v, setV] = useState<DriverEditValues>(blank());

  const rideTypes = useQuery({
    queryKey: ["ride-types", "active"],
    queryFn: () => rideTypesApi.list({ isActive: true }),
    enabled: opened,
  });

  useEffect(() => {
    if (opened && driver) {
      const docs = driver.documents ?? {};
      setV({
        name: driver.personalDetails?.name ?? "",
        email: driver.personalDetails?.email ?? "",
        phoneNumber: driver.personalDetails?.phoneNumber ?? "",
        gender: driver.personalDetails?.gender ?? "",
        dob: parseBackendDate(driver.personalDetails?.dob),
        profilePicUrl: driver.personalDetails?.profilePicUrl ?? "",
        make: driver.vehicleDetails?.make ?? "",
        model: driver.vehicleDetails?.model ?? "",
        color: driver.vehicleDetails?.color ?? "",
        vehicleCategoryID: driver.vehicleDetails?.vehicleCategoryID ?? "",
        licenseNumber: docs.licenseDetails?.licenseNumber ?? "",
        licenseExpiry: parseBackendDate(docs.licenseDetails?.expiryDate),
        licenseFront: docs.licenseDetails?.frontImageUrl ?? "",
        registrationNumber: driver.vehicleDetails?.registrationNumber ?? "",
        chassisNumber: driver.vehicleDetails?.chassisNumber ?? "",
        insuranceExpiry: parseBackendDate(docs.vehicleInsuranceDetails?.expiryDate),
        insuranceImage: docs.vehicleInsuranceDetails?.imageUrl ?? "",
        d4Expiry: parseBackendDate(docs.latraVehicleLicenseD4Details?.expiryDate),
        d4Image: docs.latraVehicleLicenseD4Details?.imageUrl ?? "",
      });
    }
  }, [opened, driver]);

  const rideTypeOptions = (rideTypes.data?.items ?? []).map((rt) => ({
    value: rt._id,
    label: en(rt.name),
  }));

  // LATRA formats; the backend rejects the whole save if either is off.
  const plateErr = plateError(v.registrationNumber);
  const licenseErr = licenseError(v.licenseNumber);

  const valid =
    v.name.trim() &&
    /\S+@\S+\.\S+/.test(v.email) &&
    v.phoneNumber.trim() &&
    !plateErr &&
    !licenseErr;

  const set = (patch: Partial<DriverEditValues>) => setV({ ...v, ...patch });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit driver"
      centered
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack>
        <Text size="sm" fw={600} c="dimmed">
          Personal
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="Name" value={v.name} onChange={(e) => set({ name: e.currentTarget.value })} required />
          <TextInput label="Phone" value={v.phoneNumber} onChange={(e) => set({ phoneNumber: e.currentTarget.value })} required />
          <TextInput label="Email" type="email" value={v.email} onChange={(e) => set({ email: e.currentTarget.value })} required />
          <Select
            label="Gender"
            data={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }]}
            value={v.gender}
            onChange={(val) => set({ gender: val ?? "" })}
            clearable
          />
          <DateInput label="Date of birth" value={v.dob} onChange={(d) => set({ dob: parseBackendDate(d as Date | string | null) })} maxDate={new Date()} clearable />
          <UploadField label="Profile photo" value={v.profilePicUrl} onChange={(key) => set({ profilePicUrl: key })} />
        </SimpleGrid>

        <Divider />
        <Text size="sm" fw={600} c="dimmed">
          Vehicle
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="Make" value={v.make} onChange={(e) => set({ make: e.currentTarget.value })} />
          <TextInput label="Model" value={v.model} onChange={(e) => set({ model: e.currentTarget.value })} />
          <TextInput label="Color" value={v.color} onChange={(e) => set({ color: e.currentTarget.value })} />
          <TextInput label="Registration no. (plate)" placeholder="T123ABC" value={v.registrationNumber} onChange={(e) => set({ registrationNumber: e.currentTarget.value })} error={plateErr} />
          <TextInput label="Chassis number" value={v.chassisNumber} onChange={(e) => set({ chassisNumber: e.currentTarget.value })} />
          <Select
            label="Ride type"
            data={rideTypeOptions}
            value={v.vehicleCategoryID}
            onChange={(val) => set({ vehicleCategoryID: val ?? "" })}
            searchable
            clearable
          />
        </SimpleGrid>

        <Divider />
        <Text size="sm" fw={600} c="dimmed">
          Documents
        </Text>
        <Text size="xs" c="dimmed">
          Driving license
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="License number" placeholder="10 digits" value={v.licenseNumber} onChange={(e) => set({ licenseNumber: e.currentTarget.value })} error={licenseErr} />
          <DateInput label="License expiry" value={v.licenseExpiry} onChange={(d) => set({ licenseExpiry: parseBackendDate(d as Date | string | null) })} clearable />
          <UploadField label="License (front)" value={v.licenseFront} onChange={(key) => set({ licenseFront: key })} uploadType="document" />
        </SimpleGrid>

        <Text size="xs" c="dimmed" mt="xs">
          Insurance &amp; LATRA D4
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <DateInput label="Insurance expiry" value={v.insuranceExpiry} onChange={(d) => set({ insuranceExpiry: parseBackendDate(d as Date | string | null) })} clearable />
          <UploadField label="Insurance" value={v.insuranceImage} onChange={(key) => set({ insuranceImage: key })} uploadType="document" />
          <DateInput label="LATRA D4 expiry" value={v.d4Expiry} onChange={(d) => set({ d4Expiry: parseBackendDate(d as Date | string | null) })} clearable />
          <UploadField label="LATRA D4 permit" value={v.d4Image} onChange={(key) => set({ d4Image: key })} uploadType="document" />
        </SimpleGrid>

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={loading} disabled={!valid} onClick={() => onSubmit(v)}>
            Save changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function blank(): DriverEditValues {
  return {
    name: "",
    email: "",
    phoneNumber: "",
    gender: "",
    dob: null,
    profilePicUrl: "",
    make: "",
    model: "",
    color: "",
    vehicleCategoryID: "",
    licenseNumber: "",
    licenseExpiry: null,
    licenseFront: "",
    registrationNumber: "",
    chassisNumber: "",
    insuranceExpiry: null,
    insuranceImage: "",
    d4Expiry: null,
    d4Image: "",
  };
}
